import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, createElementBlock as _createElementBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, renderList as _renderList } from "vue";
const _hoisted_1 = { class: "font-semibold text-h6" };
const _hoisted_2 = { class: "text-body-1 font-weight-semibold mb-2" };
const _hoisted_3 = { class: "text-body-2" };
const _hoisted_4 = { class: "d-flex align-center justify-space-between" };
const _hoisted_5 = ["href"];
const _hoisted_6 = { class: "d-flex align-center ga-3" };
const _hoisted_7 = { class: "text-body-1" };
const _hoisted_8 = { class: "d-flex align-center ga-4 mb-4 text-body-2" };
const _hoisted_9 = { class: "d-flex align-center ga-2" };
const _hoisted_10 = { class: "d-flex align-center ga-2" };
const _hoisted_11 = { class: "font-medium" };
const _hoisted_12 = { class: "space-y-2" };
const _hoisted_13 = { class: "ml-4" };
const _hoisted_14 = {
    key: 0,
    class: "mt-2"
};
const _hoisted_15 = { class: "space-y-2" };
const _hoisted_16 = {
    class: "text-body-2",
    style: { "white-space": "pre-line" }
};
const _hoisted_17 = { class: "text-body-2 text-medium-emphasis" };
const _hoisted_18 = { key: 0 };
const _hoisted_19 = { key: 1 };
import { ref, computed, onMounted, onUnmounted, watch, provide, nextTick } from "vue";
import { intakeService } from "@/services/intakeService";
import CriteriaItem from "./PolicyReviewCriteriaItem.vue";
import { decamelizeKeys } from "humps";
const MAX_POLL_ATTEMPTS = 30; // 30 attempts * 10 seconds = 5 minutes max (safety margin)
const POLL_INTERVAL_MS = 10000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PolicyReviewCriteriaCard',
    props: {
        order: {},
        currentPageId: {},
        pageManager: {}
    },
    emits: ["highlight-evidence"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const onHighlightEvidence = (payload) => {
            emit("highlight-evidence", payload);
        };
        // Initialize with a safe default structure to prevent undefined status errors
        const policyReview = ref({
            status: "processing",
            message: "Policy review is running",
        });
        const loading = ref(false);
        const criteriaTree = ref([]);
        const renderKey = ref(0);
        const criteriaState = ref({}); // Store user-modified statuses
        const pageAttachments = ref({}); // Store page attachments
        const retryingPolicyReview = ref(false);
        const checkerRunning = ref(false);
        const searching = ref(false);
        const canceling = ref(false);
        const errorSnackbar = ref(false);
        const errorSnackbarMessage = ref("");
        const searchKeyword = ref("");
        const lastSearchedKeyword = ref("");
        const selectedCandidateIndex = ref(0);
        // The manual page override for a criterion, or null if none exists. An empty array is a
        // real override (user removed every page) and must be distinguished from "no override".
        const getPageOverride = (id) => {
            if (!id)
                return null;
            const override = pageAttachments.value[id];
            return override === undefined ? null : override;
        };
        // Polling timeout and retry management
        // Policy review typically takes 3~4 minutes, so we allow up to 5 minutes
        let pollInterval = null;
        let pollAttempts = 0;
        const stopPolling = () => {
            if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
            }
            pollAttempts = 0;
        };
        const isProcessing = computed(() => {
            // Don't show processing if we have data with criteria
            if (criteriaTree.value.length > 0) {
                return false;
            }
            // Safely check status with optional chaining
            const status = policyReview.value?.status;
            return status === "processing" || loading.value;
        });
        const isBackendProcessing = computed(() => policyReview.value?.status === "processing");
        const runInFlight = computed(() => isBackendProcessing.value || checkerRunning.value);
        const showCancel = computed(() => runInFlight.value);
        const candidates = computed(() => policyReview.value?.candidates || []);
        const candidateResults = computed(() => policyReview.value?.candidateResults || {});
        const hasCandidates = computed(() => candidates.value.length > 0);
        const candidateItems = computed(() => candidates.value.map((c) => ({
            value: c.index,
            title: [c.payor, c.title].filter(Boolean).join(" — "),
        })));
        const selectedCandidate = computed(() => candidates.value.find((c) => c.index === selectedCandidateIndex.value) || null);
        const selectedPolicyUrl = computed(() => {
            const url = selectedCandidate.value?.sourceUrl || selectedCandidate.value?.pdfUrl || "";
            return url.startsWith("https://") || url.startsWith("http://") ? url : "";
        });
        const selectedHasResult = computed(() => {
            const result = candidateResults.value[String(selectedCandidateIndex.value)];
            return !!(result && result.structuredOutput);
        });
        const syncSelectedFromReview = () => {
            if (!Array.isArray(policyReview.value?.candidates))
                return;
            selectedCandidateIndex.value = policyReview.value?.selectedCandidateIndex ?? 0;
        };
        const policyInputsChanged = computed(() => {
            const inputs = policyReview.value?.inputs;
            if (!inputs || !props.order?.orderForm)
                return false;
            const norm = (val) => String(val ?? "")
                .trim()
                .toLowerCase();
            const orderForm = props.order?.orderForm;
            const insuranceCard = props.order?.insuranceCard;
            const fields = [
                [inputs.drug, orderForm?.therapyAdministration?.[0]?.name ?? ""],
                [inputs.diagnosis, orderForm?.diagnosisIcd10Code ?? ""],
                [inputs.payorName, orderForm?.extraData?.reimbursementPayorName || insuranceCard?.payorName || ""],
            ];
            return fields.some(([stored, current]) => {
                if (stored == null)
                    return false;
                return norm(stored) !== norm(current);
            });
        });
        const showErrorBanner = computed(() => {
            const status = policyReview.value?.status;
            return status === "failed" || status === "no_policy_found" || status === "missing_input";
        });
        const fallbackPayorUsed = computed(() => policyReview.value?.fallbackPayorUsed ?? null);
        const originalPayorName = computed(() => policyReview.value?.inputs?.payorName || "the order's payor");
        const errorAlertType = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found" || status === "missing_input")
                return "warning";
            return "error";
        });
        const errorTitle = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found")
                return "No Policy Found";
            if (status === "missing_input")
                return "Missing Input";
            return "Policy Review Failed";
        });
        const errorMessage = computed(() => {
            const status = policyReview.value?.status;
            if (status === "no_policy_found") {
                if (candidates.value.length === 0) {
                    const keyword = lastSearchedKeyword.value || policyReview.value?.inputs?.customSearchKeyword;
                    if (keyword) {
                        return `No results for "${keyword}".`;
                    }
                    return "Unable to find drug payor policy.";
                }
                return (policyReview.value?.error ||
                    "The policy check could not be completed. Try running it again or pick another policy.");
            }
            if (status === "missing_input")
                return policyReview.value?.error || "Missing required input fields to run policy review.";
            return policyReview.value?.error || policyReview.value?.message || "An unknown error occurred during policy review.";
        });
        const shouldShow = computed(() => !!policyReview.value || isProcessing.value);
        const overallStatus = computed(() => {
            const hasFailures = criteriaTree.value.some((c) => {
                const status = getCriterionStatus(c);
                return status === "fail";
            });
            return hasFailures ? "Needs Review" : "Approved";
        });
        // Determine criteria title based on referral status
        const criteriaTitle = computed(() => {
            const referralStatus = policyReview.value?.referralStatus ||
                policyReview.value?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            if (referralStatus === "continuation") {
                return "Continuation of Therapy Criteria";
            }
            return "Initial Approval Criteria";
        });
        // Fetch policy review data
        const fetchPolicyReview = async () => {
            loading.value = true;
            try {
                const raw = await intakeService.getPolicyReview(props.order.id);
                const response = !Array.isArray(raw?.candidates) && Array.isArray(policyReview.value?.candidates)
                    ? {
                        ...raw,
                        candidates: policyReview.value.candidates,
                        candidateResults: policyReview.value.candidateResults,
                        selectedCandidateIndex: policyReview.value.selectedCandidateIndex,
                        inputs: raw.inputs ?? policyReview.value.inputs,
                        fallbackPayorUsed: raw.fallbackPayorUsed ?? policyReview.value.fallbackPayorUsed,
                    }
                    : raw;
                // Check if response has structured output even if status is processing
                const responseHasData = !!response?.structuredOutput;
                const responseStatus = response?.status;
                let shouldReparse = false; // Track if we need to re-parse criteria
                // Always update if response has structured output (new data available)
                if (responseHasData) {
                    const currentStructuredOutput = policyReview.value?.structuredOutput;
                    const newStructuredOutput = response?.structuredOutput;
                    const pageAttachmentsData = decamelizeKeys(response?.pageAttachments) || {};
                    if (pageAttachmentsData && Object.keys(pageAttachmentsData).length > 0) {
                        pageAttachments.value = { ...pageAttachments.value, ...pageAttachmentsData };
                    }
                    const isNewData = !currentStructuredOutput || JSON.stringify(currentStructuredOutput) !== JSON.stringify(newStructuredOutput);
                    if (isNewData) {
                        shouldReparse = true;
                        criteriaTree.value = [];
                    }
                    loading.value = false;
                }
                else if (response.error ||
                    responseStatus === "completed" ||
                    responseStatus === "failed" ||
                    responseStatus === "no_policy_found" ||
                    responseStatus === "missing_input") {
                    loading.value = false;
                }
                policyReview.value = response;
                if (Array.isArray(raw?.candidates)) {
                    syncSelectedFromReview();
                }
                const structuredOutput = responseHasData ? response?.structuredOutput : policyReview.value?.structuredOutput;
                if (shouldReparse || criteriaTree.value.length === 0) {
                    if (structuredOutput?.criteria && Array.isArray(structuredOutput?.criteria)) {
                        criteriaTree.value = [...structuredOutput.criteria];
                        await setCriteriaTree(response, responseHasData);
                        policyReview.value = {
                            ...policyReview.value,
                            structuredOutput: structuredOutput,
                        };
                    }
                }
            }
            catch (error) {
                const err = error;
                const errMessage = err.response?.data?.detail || err.message || "Failed to fetch policy review";
                policyReview.value = {
                    status: "failed",
                    error: errMessage,
                    message: errMessage,
                };
            }
            finally {
                loading.value = false;
            }
        };
        const setCriteriaTree = async (response, responseHasData) => {
            // Filter parsed criteria based on referral status
            const referralStatus = (responseHasData ? response : policyReview.value)?.referralStatus ||
                (responseHasData ? response : policyReview.value)?.inputs?.initialOrContinuation ||
                props.order?.orderForm?.extraData?.referralStatus ||
                "initial";
            const filtered = filterCriteriaByReferralStatus(criteriaTree.value, referralStatus);
            // Create new array reference to trigger reactivity
            criteriaTree.value = [...filtered];
            if (criteriaTree.value.length > 0) {
                loading.value = false; // Ensure loading is stopped when we have data
                // Ensure status is set to completed so isProcessing returns false
                policyReview.value = {
                    ...policyReview.value,
                    status: "completed",
                };
                // Stop polling immediately when we have data
                stopPolling();
                // Force immediate reactive update - trigger a re-render
                await nextTick();
                renderKey.value += 1;
            }
        };
        // Filter criteria tree to show only sections relevant to referral status
        const filterCriteriaByReferralStatus = (criteria, referralStatus) => {
            if (!Array.isArray(criteria) || criteria.length === 0) {
                return criteria;
            }
            const normalizedStatus = String(referralStatus).toLowerCase().trim();
            // Define which section names are relevant for each status
            // Note: The actual section names from YAML are "Initial Approval Criteria" and "Continued Therapy Criteria"
            // "Medically Necessary Diagnoses" should always be shown regardless of referral status
            const relevantSections = {
                initial: ["Initial Approval Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
                continuation: ["Continued Therapy Criteria", "Other Approval Criteria", "Medically Necessary Diagnoses"],
            };
            const allowedSections = relevantSections[normalizedStatus] || relevantSections.initial;
            // Filter criteria recursively - sections are top-level items with type "section"
            const filterRecursive = (items, parentSectionName = null) => {
                return items
                    .map((item) => {
                    // Check if this is a section that should be included
                    const sectionName = item.text || item.name || "";
                    const isRelevantSection = allowedSections.some((allowed) => {
                        // Use exact match or case-insensitive contains for section names
                        const sectionLower = sectionName.toLowerCase();
                        const allowedLower = allowed.toLowerCase();
                        return sectionLower === allowedLower || sectionLower.includes(allowedLower);
                    });
                    // Determine the current section name (either this item if it's a section, or inherit from parent)
                    const currentSectionName = item.type === "section" ? sectionName : parentSectionName;
                    // If this item has children, filter them recursively
                    if (item.children && Array.isArray(item.children)) {
                        const filteredChildren = filterRecursive(item.children, currentSectionName);
                        // For sections (top-level items), only include if it's a relevant section
                        if (item.type === "section") {
                            // Top-level section - only include if it matches
                            if (isRelevantSection) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                        else {
                            // Nested group or criterion - include if parent section is relevant
                            // Check if parent section name matches allowed sections
                            const parentIsRelevant = currentSectionName
                                ? allowedSections.some((allowed) => {
                                    const parentLower = currentSectionName.toLowerCase();
                                    const allowedLower = allowed.toLowerCase();
                                    return parentLower === allowedLower || parentLower.includes(allowedLower);
                                })
                                : false;
                            if (parentIsRelevant) {
                                return {
                                    ...item,
                                    children: filteredChildren,
                                };
                            }
                            return null;
                        }
                    }
                    // For leaf nodes (no children), include if parent section is relevant
                    const parentIsRelevant = currentSectionName
                        ? allowedSections.some((allowed) => {
                            const parentLower = currentSectionName.toLowerCase();
                            const allowedLower = allowed.toLowerCase();
                            return parentLower === allowedLower || parentLower.includes(allowedLower);
                        })
                        : false;
                    return parentIsRelevant ? item : null;
                })
                    .filter((item) => item !== null);
            };
            return filterRecursive(criteria);
        };
        const getCriterionStatus = (criterion) => {
            // Check if user has overridden the status
            if (criterion.id && criteriaState.value[criterion.id]) {
                return criteriaState.value[criterion.id];
            }
            // For parent nodes, calculate status from children
            if (criterion.children && criterion.children.length > 0) {
                const childStatuses = criterion.children.map((child) => getCriterionStatus(child));
                if (criterion.logic === "ALL") {
                    return childStatuses.every((s) => s === "pass") ? "pass" : "fail";
                }
                else if (criterion.logic === "ONE") {
                    return childStatuses.some((s) => s === "pass") ? "pass" : "fail";
                }
            }
            return criterion.status || "pending";
        };
        const onStatusChange = async (criterionId, newStatus) => {
            if (!criterionId)
                return;
            // Update local state immediately for responsive UI
            criteriaState.value[criterionId] = newStatus;
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    criteria_state: {
                        [criterionId]: newStatus,
                    },
                });
            }
            catch (error) {
                // Error updating criteria status
                const err = error;
                // Revert on error
                delete criteriaState.value[criterionId];
                // Show user-friendly error message
                const msg = err.response?.data?.errorDetails || err.message || "Failed to update status";
                errorSnackbarMessage.value = msg;
                errorSnackbar.value = true;
            }
        };
        const onCandidateSelect = async (index) => {
            if (index === selectedCandidateIndex.value)
                return;
            const wasRunning = runInFlight.value;
            selectedCandidateIndex.value = index;
            criteriaState.value = {};
            pageAttachments.value = {};
            criteriaTree.value = [];
            const result = candidateResults.value[String(index)];
            if (result && result.structuredOutput) {
                if (wasRunning) {
                    stopPolling();
                    checkerRunning.value = false;
                    if (policyReview.value?.status === "processing") {
                        policyReview.value = { ...policyReview.value, status: null };
                    }
                }
                await runCheckerOnSelected(true);
                return;
            }
            if (wasRunning) {
                stopPolling();
                checkerRunning.value = false;
                try {
                    const response = await intakeService.cancelPolicyReview(props.order.id);
                    policyReview.value = { ...response, selectedCandidateIndex: index };
                }
                catch (error) {
                    const err = error;
                    errorSnackbarMessage.value = err.response?.data?.error || err.message || "Failed to cancel policy review";
                    errorSnackbar.value = true;
                }
            }
        };
        const runCheckerOnSelected = async (fromCache = false) => {
            if (checkerRunning.value)
                return;
            criteriaState.value = {};
            if (!fromCache) {
                checkerRunning.value = true;
                if (policyReview.value) {
                    policyReview.value = { ...policyReview.value, status: "processing", error: undefined };
                }
            }
            try {
                const response = await intakeService.triggerPolicyReview(props.order.id, {
                    candidateIndex: selectedCandidateIndex.value,
                });
                if (response.status === "completed" && response.structuredOutput) {
                    policyReview.value = response;
                    const criteria = response.structuredOutput?.criteria;
                    criteriaTree.value = Array.isArray(criteria) ? [...criteria] : [];
                    await setCriteriaTree(response, true);
                }
                else {
                    policyReview.value = { ...policyReview.value, ...response };
                    criteriaTree.value = [];
                }
            }
            catch (error) {
                const err = error;
                policyReview.value = {
                    ...policyReview.value,
                    status: "failed",
                    error: err.response?.data?.error || err.response?.data?.detail || err.message || "Failed to run policy checker",
                };
            }
            finally {
                checkerRunning.value = false;
            }
        };
        const cancelRun = async () => {
            if (canceling.value)
                return;
            canceling.value = true;
            stopPolling();
            try {
                const response = await intakeService.cancelPolicyReview(props.order.id);
                checkerRunning.value = false;
                criteriaTree.value = [];
                policyReview.value = response;
                if (response?.selectedCandidateIndex != null) {
                    selectedCandidateIndex.value = response.selectedCandidateIndex;
                }
            }
            catch (error) {
                const err = error;
                errorSnackbarMessage.value = err.response?.data?.error || err.message || "Failed to cancel policy review";
                errorSnackbar.value = true;
            }
            finally {
                canceling.value = false;
            }
        };
        const retryPolicyReview = async () => {
            if (retryingPolicyReview.value)
                return;
            retryingPolicyReview.value = true;
            loading.value = true;
            criteriaTree.value = [];
            if (policyReview.value) {
                policyReview.value = { ...policyReview.value, status: "processing", running: true, policyDocumentUrl: null };
            }
            try {
                const response = await intakeService.triggerPolicyReview(props.order.id);
                // Ensure response exists before using it
                if (!response) {
                    throw new Error("No response received from policy review trigger");
                }
                policyReview.value = response;
            }
            catch (error) {
                // Extract detailed error message from response
                const err = error;
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to trigger policy review";
                policyReview.value = {
                    status: "failed",
                    error: errorMessage,
                    message: errorMessage,
                };
            }
            finally {
                retryingPolicyReview.value = false;
                loading.value = false;
            }
        };
        const doSearch = async () => {
            if (searching.value)
                return;
            const keyword = (searchKeyword.value ?? "").trim();
            if (!keyword)
                return;
            searching.value = true;
            try {
                const response = await intakeService.searchPolicies(props.order.id, keyword);
                stopPolling();
                checkerRunning.value = false;
                lastSearchedKeyword.value = keyword;
                searchKeyword.value = "";
                criteriaTree.value = [];
                criteriaState.value = {};
                pageAttachments.value = {};
                policyReview.value = response;
                selectedCandidateIndex.value = response?.selectedCandidateIndex ?? 0;
            }
            catch (error) {
                stopPolling();
                checkerRunning.value = false;
                if (policyReview.value?.status === "processing") {
                    policyReview.value = { ...policyReview.value, status: null, running: false };
                }
                const err = error;
                errorSnackbarMessage.value = err.response?.data?.error || err.message || "Policy search failed";
                errorSnackbar.value = true;
            }
            finally {
                searching.value = false;
            }
        };
        // Recursively find a criterion node by id in the (filtered) criteria tree.
        const findCriterion = (criteria, id) => {
            for (const criterion of criteria) {
                if (criterion.id === id)
                    return criterion;
                if (criterion.children) {
                    const found = findCriterion(criterion.children, id);
                    if (found)
                        return found;
                }
            }
            return null;
        };
        // The AI-detected page IDs for a criterion: its pageNumbers/pageRef indices mapped to page
        // IDs via the page manager. Used to seed a manual override on the first edit (copy-on-write).
        const getAiPageIds = (criterionId) => {
            const criterion = findCriterion(criteriaTree.value, criterionId);
            if (!criterion || !props.pageManager)
                return [];
            const indices = criterion.pageNumbers && criterion.pageNumbers.length > 0
                ? criterion.pageNumbers
                : criterion.pageRef !== undefined
                    ? [criterion.pageRef]
                    : [];
            return indices
                .map((index) => props.pageManager?.pages[index]?.id)
                .filter((id) => id !== undefined && id !== null)
                .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
                .filter((id) => !Number.isNaN(id));
        };
        const onPageAction = async (criterionId, action, pageId) => {
            if (!criterionId)
                return;
            const numericPageId = typeof pageId === "string" ? parseInt(pageId, 10) : pageId;
            if (!numericPageId)
                return;
            // Copy-on-write: the first edit to a criterion seeds the override from the AI-detected
            // pages, after which page_attachments is the authoritative list for that criterion. This
            // is what lets an AI-assigned page be removed (it becomes a normal override entry first).
            let aiPageBaseline = null;
            if (pageAttachments.value[criterionId] === undefined) {
                aiPageBaseline = getAiPageIds(criterionId);
                pageAttachments.value[criterionId] = [...aiPageBaseline];
            }
            if (action === "add") {
                if (!pageAttachments.value[criterionId].includes(numericPageId)) {
                    pageAttachments.value[criterionId].push(numericPageId);
                }
            }
            else if (action === "remove") {
                pageAttachments.value[criterionId] = pageAttachments.value[criterionId].filter((id) => id !== numericPageId);
            }
            // Update in backend
            try {
                await intakeService.updatePolicyReview(props.order.id, {
                    page_attachments: {
                        [criterionId]: pageAttachments.value[criterionId] || [],
                    },
                    ...(aiPageBaseline !== null && { ai_page_baseline: { [criterionId]: aiPageBaseline } }),
                });
            }
            catch (error) {
                // Error updating page attachments
                const err = error;
                // Show user-friendly error message
                const msg = err.response?.data?.errorDetails || err.message || "Failed to update page";
                errorSnackbarMessage.value = msg;
                errorSnackbar.value = true;
            }
        };
        // Poll for updates if processing (but don't poll if we have completed data)
        // Watch both isProcessing and policyReview status to ensure we poll when needed
        watch([() => isProcessing.value, () => policyReview.value?.status], ([processing, status]) => {
            // Check if we have truly completed data (status is completed AND we have structured output)
            const hasCompletedData = status === "completed" && policyReview.value?.structuredOutput && criteriaTree.value.length > 0;
            // Start polling if:
            // 1. Status is processing OR we're in processing state
            // 2. We don't already have a poll interval running
            // 3. We don't have truly completed data
            const shouldPoll = (processing || status === "processing") && !pollInterval && !hasCompletedData;
            if (shouldPoll) {
                pollAttempts = 0; // Reset attempts when starting new polling
                pollInterval = setInterval(() => {
                    pollAttempts++;
                    // Stop polling if we've exceeded max attempts
                    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
                        stopPolling();
                        // Set status to failed with timeout message
                        if (policyReview.value && typeof policyReview.value === "object") {
                            const currentStatus = policyReview.value.status;
                            if (currentStatus === "processing") {
                                policyReview.value.status = "failed";
                                policyReview.value.error =
                                    "Policy review is taking longer than expected (over 5 minutes). The review may still be processing. Please refresh the page or contact support if this persists.";
                            }
                        }
                        loading.value = false;
                        return;
                    }
                    // Always fetch to get the latest status from the API
                    // This ensures we detect completion as soon as it happens
                    fetchPolicyReview();
                }, POLL_INTERVAL_MS);
            }
            else if (pollInterval && !processing && status !== "processing") {
                stopPolling();
            }
        }, { immediate: true } // Run immediately to check initial state
        );
        // Provide helper functions to child components
        provide("getCriterionStatus", getCriterionStatus);
        provide("getPageAttachments", (id) => getPageOverride(id));
        onMounted(() => {
            fetchPolicyReview();
        });
        onUnmounted(() => {
            // Clean up polling interval when component is unmounted
            stopPolling();
        });
        return (_ctx, _cache) => {
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_spacer = _resolveComponent("v-spacer");
            const _component_v_divider = _resolveComponent("v-divider");
            const _component_v_expansion_panel_title = _resolveComponent("v-expansion-panel-title");
            const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text");
            const _component_v_expansion_panel = _resolveComponent("v-expansion-panel");
            const _component_v_expansion_panels = _resolveComponent("v-expansion-panels");
            const _component_v_snackbar = _resolveComponent("v-snackbar");
            const _component_v_card = _resolveComponent("v-card");
            return (shouldShow.value)
                ? (_openBlock(), _createBlock(_component_v_card, {
                    key: renderKey.value,
                    class: "mt-4 mb-4 policy-review-criteria-card",
                    color: "surface",
                    variant: "elevated",
                    elevation: "0"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_v_card_title, { class: "d-flex align-center ga-2" }, {
                            default: _withCtx(() => [
                                _createElementVNode("h2", _hoisted_1, _toDisplayString(criteriaTitle.value), 1)
                            ]),
                            _: 1
                        }),
                        (fallbackPayorUsed.value || showErrorBanner.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, {
                                key: 0,
                                class: "pb-0"
                            }, {
                                default: _withCtx(() => [
                                    (fallbackPayorUsed.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 0,
                                            type: "info",
                                            variant: "tonal",
                                            density: "compact",
                                            class: "mb-2"
                                        }, {
                                            default: _withCtx(() => [
                                                _cache[3] || (_cache[3] = _createTextVNode(" No policy found for ", -1)),
                                                _createElementVNode("strong", null, _toDisplayString(originalPayorName.value), 1),
                                                _cache[4] || (_cache[4] = _createTextVNode(" — showing the ", -1)),
                                                _createElementVNode("strong", null, _toDisplayString(fallbackPayorUsed.value), 1),
                                                _cache[5] || (_cache[5] = _createTextVNode(" policy for this drug instead. ", -1))
                                            ]),
                                            _: 1,
                                            __: [3, 4, 5]
                                        }))
                                        : _createCommentVNode("", true),
                                    (showErrorBanner.value)
                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                            key: 1,
                                            type: errorAlertType.value,
                                            variant: "outlined",
                                            class: "mb-0"
                                        }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_2, _toDisplayString(errorTitle.value), 1),
                                                _createElementVNode("div", _hoisted_3, _toDisplayString(errorMessage.value), 1)
                                            ]),
                                            _: 1
                                        }, 8, ["type"]))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true),
                        (policyInputsChanged.value && !isBackendProcessing.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, {
                                key: 1,
                                class: "py-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_alert, {
                                        type: "info",
                                        variant: "tonal",
                                        density: "compact"
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_4, [
                                                _cache[7] || (_cache[7] = _createElementVNode("span", null, "A change to the policy fields was detected.", -1)),
                                                _createVNode(_component_v_btn, {
                                                    size: "small",
                                                    color: "primary",
                                                    variant: "outlined",
                                                    loading: retryingPolicyReview.value,
                                                    disabled: searching.value || canceling.value,
                                                    class: "ml-4",
                                                    onClick: retryPolicyReview
                                                }, {
                                                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                                                        _createTextVNode(" Re-run Policy Review ", -1)
                                                    ])),
                                                    _: 1,
                                                    __: [6]
                                                }, 8, ["loading", "disabled"])
                                            ])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true),
                        _createVNode(_component_v_card_text, { class: "pb-2" }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_text_field, {
                                    modelValue: searchKeyword.value,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((searchKeyword).value = $event)),
                                    label: "Search policies by keyword",
                                    density: "compact",
                                    variant: "outlined",
                                    clearable: "",
                                    maxlength: "200",
                                    counter: "",
                                    "hide-details": "auto",
                                    disabled: searching.value,
                                    onKeyup: _withKeys(doSearch, ["enter"])
                                }, {
                                    append: _withCtx(() => [
                                        _createVNode(_component_v_btn, {
                                            color: "primary",
                                            variant: "outlined",
                                            loading: searching.value,
                                            disabled: !searchKeyword.value?.trim() || searching.value,
                                            onClick: doSearch
                                        }, {
                                            default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                _createTextVNode(" Search ", -1)
                                            ])),
                                            _: 1,
                                            __: [8]
                                        }, 8, ["loading", "disabled"])
                                    ]),
                                    _: 1
                                }, 8, ["modelValue", "disabled"])
                            ]),
                            _: 1
                        }),
                        (hasCandidates.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, {
                                key: 2,
                                class: "py-2",
                                style: { "white-space": "normal" }
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_select, {
                                        "model-value": selectedCandidateIndex.value,
                                        items: candidateItems.value,
                                        "item-value": "value",
                                        "item-title": "title",
                                        density: "compact",
                                        variant: "outlined",
                                        "hide-details": "",
                                        disabled: searching.value || canceling.value,
                                        style: { "max-width": "500px" },
                                        "onUpdate:modelValue": onCandidateSelect
                                    }, null, 8, ["model-value", "items", "disabled"]),
                                    (selectedPolicyUrl.value)
                                        ? (_openBlock(), _createElementBlock("a", {
                                            key: 0,
                                            href: selectedPolicyUrl.value,
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                            class: "text-primary text-decoration-none policy-source-link mt-1 d-inline-flex align-center"
                                        }, [
                                            _createVNode(_component_v_icon, {
                                                icon: "mdi-open-in-new",
                                                size: "x-small",
                                                class: "mr-1"
                                            }),
                                            _cache[9] || (_cache[9] = _createTextVNode(" View original policy ", -1))
                                        ], 8, _hoisted_5))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true),
                        (isProcessing.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 3 }, {
                                default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_6, [
                                        _createVNode(_component_v_progress_circular, {
                                            indeterminate: "",
                                            color: "primary",
                                            size: "24"
                                        }),
                                        _createElementVNode("span", _hoisted_7, [
                                            _cache[10] || (_cache[10] = _createTextVNode(" Policy review is running", -1)),
                                            (policyReview.value?.estimatedSeconds)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                    _createTextVNode(", it usually takes " + _toDisplayString(policyReview.value.estimatedSeconds) + " seconds to execute", 1)
                                                ], 64))
                                                : _createCommentVNode("", true),
                                            _cache[11] || (_cache[11] = _createTextVNode("... ", -1))
                                        ]),
                                        _createVNode(_component_v_spacer),
                                        (showCancel.value)
                                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                                key: 0,
                                                variant: "text",
                                                size: "small",
                                                loading: canceling.value,
                                                onClick: cancelRun
                                            }, {
                                                default: _withCtx(() => _cache[12] || (_cache[12] = [
                                                    _createTextVNode("Cancel", -1)
                                                ])),
                                                _: 1,
                                                __: [12]
                                            }, 8, ["loading"]))
                                            : _createCommentVNode("", true)
                                    ])
                                ]),
                                _: 1
                            }))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                (criteriaTree.value.length > 0)
                                    ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_8, [
                                                _createElementVNode("div", _hoisted_9, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: "mdi-check-circle",
                                                        color: "success",
                                                        size: "small"
                                                    }),
                                                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-medium-emphasis" }, "Present", -1))
                                                ]),
                                                _createElementVNode("div", _hoisted_10, [
                                                    _createVNode(_component_v_icon, {
                                                        icon: "mdi-close-circle",
                                                        color: "error",
                                                        size: "small"
                                                    }),
                                                    _cache[14] || (_cache[14] = _createElementVNode("span", { class: "text-medium-emphasis" }, "Missing", -1))
                                                ]),
                                                _createVNode(_component_v_divider, {
                                                    vertical: "",
                                                    class: "h-4"
                                                }),
                                                _createElementVNode("div", _hoisted_11, [
                                                    _cache[15] || (_cache[15] = _createTextVNode(" Status: ", -1)),
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(overallStatus.value === 'Approved' ? 'text-success' : 'text-error')
                                                    }, _toDisplayString(overallStatus.value), 3)
                                                ])
                                            ]),
                                            _createElementVNode("div", _hoisted_12, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criteriaTree.value, (criterion) => {
                                                    return (_openBlock(), _createElementBlock(_Fragment, {
                                                        key: criterion.id
                                                    }, [
                                                        (criterion.text === 'Medically Necessary Diagnoses' && criterion.children)
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                _createVNode(CriteriaItem, {
                                                                    criterion: { ...criterion, children: [] },
                                                                    "current-page-id": _ctx.currentPageId,
                                                                    "page-manager": _ctx.pageManager,
                                                                    status: getCriterionStatus(criterion),
                                                                    "attached-pages": getPageOverride(criterion.id),
                                                                    onStatusChange: onStatusChange,
                                                                    onPageAction: onPageAction,
                                                                    onHighlightEvidence: onHighlightEvidence
                                                                }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]),
                                                                _createElementVNode("div", _hoisted_13, [
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) === 'pass'), (child) => {
                                                                        return (_openBlock(), _createBlock(CriteriaItem, {
                                                                            key: child.id,
                                                                            criterion: child,
                                                                            "current-page-id": _ctx.currentPageId,
                                                                            "page-manager": _ctx.pageManager,
                                                                            status: getCriterionStatus(child),
                                                                            "attached-pages": getPageOverride(child.id),
                                                                            onStatusChange: onStatusChange,
                                                                            onPageAction: onPageAction,
                                                                            onHighlightEvidence: onHighlightEvidence
                                                                        }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                    }), 128)),
                                                                    (criterion.children.filter((c) => getCriterionStatus(c) !== 'pass').length > 0)
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                                                            _createVNode(_component_v_expansion_panels, {
                                                                                variant: "accordion",
                                                                                class: "medically-necessary-accordion"
                                                                            }, {
                                                                                default: _withCtx(() => [
                                                                                    _createVNode(_component_v_expansion_panel, null, {
                                                                                        default: _withCtx(() => [
                                                                                            _createVNode(_component_v_expansion_panel_title, { class: "text-body-2 text-center" }, {
                                                                                                default: _withCtx(() => _cache[16] || (_cache[16] = [
                                                                                                    _createTextVNode(" View full list of diagnoses ", -1)
                                                                                                ])),
                                                                                                _: 1,
                                                                                                __: [16]
                                                                                            }),
                                                                                            _createVNode(_component_v_expansion_panel_text, null, {
                                                                                                default: _withCtx(() => [
                                                                                                    _createElementVNode("div", _hoisted_15, [
                                                                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criterion.children.filter((c) => getCriterionStatus(c) !== 'pass'), (child) => {
                                                                                                            return (_openBlock(), _createBlock(CriteriaItem, {
                                                                                                                key: child.id,
                                                                                                                criterion: child,
                                                                                                                "current-page-id": _ctx.currentPageId,
                                                                                                                "page-manager": _ctx.pageManager,
                                                                                                                status: getCriterionStatus(child),
                                                                                                                "attached-pages": getPageOverride(child.id),
                                                                                                                onStatusChange: onStatusChange,
                                                                                                                onPageAction: onPageAction,
                                                                                                                onHighlightEvidence: onHighlightEvidence
                                                                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]));
                                                                                                        }), 128))
                                                                                                    ])
                                                                                                ]),
                                                                                                _: 2
                                                                                            }, 1024)
                                                                                        ]),
                                                                                        _: 2
                                                                                    }, 1024)
                                                                                ]),
                                                                                _: 2
                                                                            }, 1024)
                                                                        ]))
                                                                        : _createCommentVNode("", true)
                                                                ])
                                                            ], 64))
                                                            : (_openBlock(), _createBlock(CriteriaItem, {
                                                                key: 1,
                                                                criterion: criterion,
                                                                "current-page-id": _ctx.currentPageId,
                                                                "page-manager": _ctx.pageManager,
                                                                status: getCriterionStatus(criterion),
                                                                "attached-pages": getPageOverride(criterion.id),
                                                                onStatusChange: onStatusChange,
                                                                onPageAction: onPageAction,
                                                                onHighlightEvidence: onHighlightEvidence
                                                            }, null, 8, ["criterion", "current-page-id", "page-manager", "status", "attached-pages"]))
                                                    ], 64));
                                                }), 128))
                                            ])
                                        ]),
                                        _: 1
                                    }))
                                    : (hasCandidates.value && !selectedHasResult.value)
                                        ? (_openBlock(), _createBlock(_component_v_card_text, { key: 1 }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_btn, {
                                                    color: "primary",
                                                    variant: "flat",
                                                    loading: checkerRunning.value,
                                                    disabled: searching.value || canceling.value,
                                                    onClick: _cache[1] || (_cache[1] = ($event) => (runCheckerOnSelected()))
                                                }, {
                                                    default: _withCtx(() => _cache[17] || (_cache[17] = [
                                                        _createTextVNode(" Run policy checker ", -1)
                                                    ])),
                                                    _: 1,
                                                    __: [17]
                                                }, 8, ["loading", "disabled"])
                                            ]),
                                            _: 1
                                        }))
                                        : (policyReview.value?.structuredOutput?.textInsideCriteriaDropdown && criteriaTree.value.length === 0)
                                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 2 }, {
                                                default: _withCtx(() => [
                                                    _cache[18] || (_cache[18] = _createElementVNode("div", { class: "text-body-2 mb-2" }, [
                                                        _createElementVNode("strong", null, "Policy Criteria:")
                                                    ], -1)),
                                                    _createVNode(_component_v_expansion_panels, null, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_expansion_panel, null, {
                                                                default: _withCtx(() => [
                                                                    _createVNode(_component_v_expansion_panel_title, null, {
                                                                        default: _withCtx(() => [
                                                                            _createTextVNode(_toDisplayString(policyReview.value?.structuredOutput?.textHeaderCriteriaDropdown || "Policy Criteria"), 1)
                                                                        ]),
                                                                        _: 1
                                                                    }),
                                                                    _createVNode(_component_v_expansion_panel_text, null, {
                                                                        default: _withCtx(() => [
                                                                            _createElementVNode("div", _hoisted_16, _toDisplayString(policyReview.value?.structuredOutput?.textInsideCriteriaDropdown || ""), 1)
                                                                        ]),
                                                                        _: 1
                                                                    })
                                                                ]),
                                                                _: 1
                                                            })
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1,
                                                __: [18]
                                            }))
                                            : (!showErrorBanner.value)
                                                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 3 }, {
                                                    default: _withCtx(() => [
                                                        _createElementVNode("div", _hoisted_17, [
                                                            (policyReview.value?.status === 'completed')
                                                                ? (_openBlock(), _createElementBlock("div", _hoisted_18, " Policy review completed, but no criteria data is available. "))
                                                                : (_openBlock(), _createElementBlock("div", _hoisted_19, "No approval criteria available."))
                                                        ])
                                                    ]),
                                                    _: 1
                                                }))
                                                : _createCommentVNode("", true)
                            ], 64)),
                        _createVNode(_component_v_snackbar, {
                            modelValue: errorSnackbar.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((errorSnackbar).value = $event)),
                            color: "error",
                            timeout: "5000",
                            location: "top"
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(errorSnackbarMessage.value), 1)
                            ]),
                            _: 1
                        }, 8, ["modelValue"])
                    ]),
                    _: 1
                }))
                : _createCommentVNode("", true);
        };
    }
});
